import dayjs, { Dayjs } from 'dayjs'
import { useUI } from '../hooks/useUI'
import { useCompanySpaces } from '../queries/useCompanyQueries'
import { forwardRef, ForwardedRef } from 'react'
import { adjustColor } from '../theme/ajustColor'

interface CalendarProps {
  value: Dayjs | null
  onChange: (date: Dayjs) => void
  onChangeComplete: (date: Dayjs) => void
}

const Calendar = forwardRef(
  ({ value, onChange, onChangeComplete }: CalendarProps, ref: ForwardedRef<HTMLDivElement>) => {
    const { darkMode } = useUI()
    const { data: companyData } = useCompanySpaces()
    const primaryColor = companyData?.body?.content[0]?.color || '#0A3A7A'
    const colors = adjustColor(primaryColor, darkMode ? 'dark' : 'light')

    // Estilos para diferentes estados
    const containerStyle = {
      backgroundColor: colors.backgroundBase,
      color: colors.text
    } as React.CSSProperties

    const selectedStyle = {
      backgroundColor: colors.main,
      color: colors.contrast,
      '--tw-ring-color': colors.main,
      ':hover': {
        backgroundColor: colors.hover
      }
    } as React.CSSProperties

    const todayStyle = {
      backgroundColor: colors.light,
      color: colors.main,
      '--tw-ring-color': colors.main
    } as React.CSSProperties

    const focusStyle = {
      '--tw-ring-color': colors.main
    } as React.CSSProperties

    return (
      <div ref={ref} className="flex justify-center items-center w-full py-2">
        <div
          style={containerStyle}
          className="w-full max-w-2xl rounded-xl shadow-lg overflow-hidden transform transition-all duration-200"
        >
          <div className="p-3 transition-colors duration-200">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => {
                  const prevMonth = dayjs(value || dayjs()).subtract(1, 'month')
                  onChange(prevMonth)
                }}
                style={focusStyle}
                className={`p-1.5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 hover:bg-opacity-10 hover:bg-current`}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <h2 className="text-base sm:text-lg font-semibold">
                {value ? value.format('MMMM YYYY') : dayjs().format('MMMM YYYY')}
              </h2>
              <button
                onClick={() => {
                  const nextMonth = dayjs(value || dayjs()).add(1, 'month')
                  onChange(nextMonth)
                }}
                style={focusStyle}
                className={`p-1.5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 hover:bg-opacity-10 hover:bg-current`}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {['D', 'L', 'M', 'X', 'J', 'V', 'S'].map((day, index) => (
                <div key={index} className="text-center font-medium text-xs py-1 text-opacity-60">
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {(() => {
                const currentDate = value || dayjs()
                const firstDayOfMonth = dayjs(currentDate).startOf('month')
                const daysInMonth = currentDate.daysInMonth()
                const dayOfWeek = firstDayOfMonth.day()

                const minSelectableDate = dayjs().add(2, 'hour')

                const days = []

                // Add empty cells for days before the first day of month
                for (let i = 0; i < dayOfWeek; i++) {
                  days.push(<div key={`empty-${i}`} className="h-[45px] w-full"></div>)
                }

                // Add cells for each day of the month
                for (let i = 1; i <= daysInMonth; i++) {
                  const date = dayjs(firstDayOfMonth).date(i)
                  const isSelected =
                    value && date.format('YYYY-MM-DD') === value.format('YYYY-MM-DD')
                  const isDisabled = date.isBefore(minSelectableDate, 'day')
                  const isToday = date.format('YYYY-MM-DD') === dayjs().format('YYYY-MM-DD')

                  days.push(
                    <div key={i} className="h-[45px] w-full relative">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button
                          disabled={isDisabled}
                          onClick={() => {
                            if (!isDisabled) {
                              onChange(date)
                              onChangeComplete(date)
                            }
                          }}
                          style={isSelected ? selectedStyle : isToday ? todayStyle : focusStyle}
                          className={`
                          w-[35px] h-[35px] flex items-center justify-center rounded-full text-sm font-medium
                          transition-all duration-200 ease-in-out
                          focus:outline-none focus:ring-1 focus:ring-offset-1
                          ${!isSelected && !isToday && 'hover:bg-opacity-10 hover:bg-current'}
                          ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                        >
                          {i}
                        </button>
                      </div>
                    </div>
                  )
                }

                return days
              })()}
            </div>

            <div className="mt-2 text-xs text-center font-medium text-opacity-60">
              {value ? `Fecha seleccionada: ${value.format('DD/MM/YYYY')}` : 'Selecciona una fecha'}
            </div>
          </div>
        </div>
      </div>
    )
  }
)

Calendar.displayName = 'Calendar'

export default Calendar
